import { useEffect, useRef, useMemo, useCallback } from "react";
import "./styles/JobStatusPanel.css";
import toast from "react-hot-toast";

export type JobStatus = "Queued" | "Running" | "Completed" | "Failed";

export interface JobUpdatePayload {
  jobId: string;
  status: JobStatus;
  message?: string;
  outputUrl?: string;
  suggestedFilename?: string;
  failReason?: string;
  updatedAt?: Date;
  autoDownload?: boolean
  outputFileName?: string;
}

export interface JobStatusPanelProps {
  jobId: string;
  payload: JobUpdatePayload | null;
  connected: boolean;
  className?: string;
  forceClientFilename?: boolean;
}

export default function JobStatusPanel({
  jobId,
  payload,
  connected,
  className,
  forceClientFilename = false,
}: JobStatusPanelProps) {
  const startedRef = useRef(false);

  const status: JobStatus | "idle" = useMemo(
    () => payload?.status ?? "idle",
    [payload]
  );

  const autoDownload = useMemo(() => {
    if (!payload?.updatedAt) {
      return false;
    }
    const updatedAt = new Date(payload.updatedAt);

    if (isNaN(updatedAt.getTime())) {
      return false;
    }

    const now = new Date();
    const diff = (now.getTime() - updatedAt.getTime()) / 1000;

    if (diff < 0 || diff >= 40) {
      return false;
    }

    return payload.autoDownload;
  }, [payload]);

  const { statusLabel, statusSubtitle, statusEmoji } = useMemo(() => {
    switch (status) {
      case "Queued":
        return {
          statusLabel: "Queued",
          statusSubtitle: "We’ve got your job. It will start shortly.",
          statusEmoji: "⏳",
        };
      case "Running":
        return {
          statusLabel: "Processing",
          statusSubtitle:
            "We’re clipping, converting and polishing your media.",
          statusEmoji: "⚙️",
        };
      case "Completed":
        return {
          statusLabel: "Completed",
          statusSubtitle: "Your file is ready. You can download it below.",
          statusEmoji: "✅",
        };
      case "Failed":
        return {
          statusLabel: "Failed",
          statusSubtitle: "Something went wrong during processing.",
          statusEmoji: "⚠️",
        };
      default:
        return {
          statusLabel: "Connecting…",
          statusSubtitle: "Stay on this page while we connect to the job.",
          statusEmoji: "🔄",
        };
    }
  }, [status]);

  const lastUpdated =
    payload?.updatedAt &&
    new Date(payload.updatedAt).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  // --- Auto-download on completion (unchanged) ---
  useEffect(() => {
    if (
      !autoDownload ||
      !payload ||
      payload.status !== "Completed" ||
      !payload.outputUrl
    ) {
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        if (forceClientFilename || payload.suggestedFilename) {
          const res = await fetch(payload.outputUrl!, {
            credentials: "include",
          });
          if (!res.ok) throw new Error("Download failed");
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          if (payload.suggestedFilename) a.download = payload.suggestedFilename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } else {
          window.location.assign(payload.outputUrl!);
        }
      } catch (e) {
        startedRef.current = false;
      }
    })();
  }, [autoDownload, forceClientFilename, payload]);

  // simple progress index for the bar
  const stepIndex = (() => {
    switch (status) {
      case "idle":
      case "Queued":
        return 0;
      case "Running":
        return 1;
      case "Completed":
      case "Failed":
        return 2;
      default:
        return 0;
    }
  })();

  const handleCopyJobId = useCallback(async () => {
    if (!jobId) return;
    try {
      await navigator.clipboard.writeText(jobId);
      toast.success("Job ID copied to clipboard");
    } catch {
    }
  }, [jobId]);

  return (
    <section
      className={`job-panel job-panel--${status} ${className ?? ""}`}
      aria-live="polite"
    >
      <div className="job-panel-inner">
        {/* Header */}
        <header className="job-header">
          <div className="job-header-left">
            <p className="job-tag">Job status</p>
            <h1 className="job-main-title">
              {statusEmoji} {statusLabel}
            </h1>
            <p className="job-id-label">
              Job ID{" "}
              <span
                className="job-id-pill"
                title="Click to copy Job ID"
                onClick={handleCopyJobId}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCopyJobId();
                  }
                }}
              >
                {jobId}
              </span>
            </p>
            {payload?.outputFileName &&
              <p className="job-id-label">
                File Name{" "}
                <span className="job-id-pill" title={jobId}>
                  {payload.outputFileName}
                </span>
              </p>}
          </div>

          <div className="job-header-right">
            <div
              className={`job-live-pill ${connected ? "job-live-pill--on" : "job-live-pill--off"
                }`}
            >
              <span className="job-live-dot" />
              {connected ? "Live updates" : "Offline"}
            </div>
            {payload?.message && (
              <div className="job-small-chip">Waiting for updates…</div>
            )}
          </div>
        </header>

        {/* Main */}
        <div className="job-main">
          <p className="job-subtitle">{statusSubtitle}</p>

          {payload?.message && (
            <p className="job-message">
              <span className="label">Latest:</span> {payload.message}
            </p>
          )}

          <div className="job-progress-wrapper">
            <div className="job-progress-bar">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`job-progress-step ${i < stepIndex
                    ? "job-progress-step--done"
                    : i === stepIndex
                      ? "job-progress-step--active"
                      : ""
                    } ${status === "Failed" && i === stepIndex ? "error" : ""}`}
                />
              ))}
            </div>
            <div className="job-progress-labels">
              <span>Queued</span>
              <span>Processing</span>
              <span>{status === "Failed" ? "Error" : "Ready"}</span>
            </div>
          </div>

          <div className="job-meta-row">
            <span className="job-meta">
              <span className="label">Raw status:</span>{" "}
              <span className="job-meta-status">
                {payload?.status ?? "waiting"}
              </span>
            </span>
            {lastUpdated && (
              <span className="job-meta">
                <span className="label">Last update:</span> {lastUpdated}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="job-footer">
          <div className="job-actions">
            {payload?.status === "Completed" && payload.outputUrl && (
              <a
                className="btn-primary"
                href={payload.outputUrl}
                download={payload.suggestedFilename}
              >
                Download
              </a>
            )}

            {payload?.status === "Failed" && (
              <div className="job-error">
                <span className="job-error-title">Processing failed</span>
                <span className="job-error-detail">
                  {payload.failReason ?? "Unknown error occurred."}
                </span>
              </div>
            )}
          </div>

          <details className="job-details">
            <summary>Technical details</summary>
            <pre className="job-pre">
              {payload
                ? JSON.stringify(payload, null, 2)
                : "Waiting for first update…"}
            </pre>
          </details>
        </footer>
      </div>
    </section>
  );
}
